import { useQuery } from "@tanstack/react-query"
import { Info } from "lucide-react"

import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

import { fetchPagoDeImpuestos } from "@/routes/holding/api"

/**
 * CUÁNTO SE PAGÓ DE IMPUESTOS Y CUÁNTO VIENE.
 *
 * Cesar, punto 13: «en holding "obligaciones" debería de ser en verdad pago de
 * impuestos (o agrega una parte de pago de impuestos) tanto para ver el real mes
 * a mes como también del Q y año y proyectado».
 *
 * ===========================================================================
 * «O AGREGA UNA PARTE» — Y SE AGREGA, NO SE SUSTITUYE
 * ===========================================================================
 *
 * El mapa de obligaciones contesta **cuándo** y **ante quién**: qué hay que
 * presentar este mes y qué va a haber que hacer a mano. Esto contesta **cuánto**.
 * Son dos preguntas y las dos se hacen delante de esta pestaña, pero la segunda
 * no estaba.
 *
 * Reemplazar el mapa por el dinero habría perdido lo único que avisa de una
 * presentación que nadie preparó — un vencimiento no tiene importe hasta que se
 * calcula, así que no habría aparecido en ninguna tabla de pagos—. Va encima,
 * porque el dinero es lo que se mira primero.
 *
 * ---------------------------------------------------------------------------
 * LO PAGADO Y LO PROYECTADO NO COMPARTEN COLUMNA
 *
 * Las dos series salen de la misma tabla y las separa el corte (ver
 * `corte_real`). En la tabla van en dos filas distintas y el trimestre que el
 * corte parte por la mitad lleva su marca: «Q4: 5.403» con octubre pagado y
 * noviembre y diciembre estimados es la cifra con la que alguien aparta de menos.
 *
 * Y el pie dice lo que más se confunde: esto es CAJA, no el gasto por impuestos.
 * El impuesto se devenga un mes y se paga al siguiente, así que las dos series
 * están desplazadas y la que sirve para tener el dinero apartado es ésta.
 */
export function PagoDeImpuestos({ anio }: { anio: number }) {
  const { activeTenantId } = useAuth()
  const q = useQuery({
    queryKey: ["pago-de-impuestos", activeTenantId, anio],
    queryFn: () => fetchPagoDeImpuestos(anio),
    enabled: Boolean(activeTenantId),
  })
  const d = q.data

  const meses = Array.from({ length: 12 }, (_, i) => String(i + 1))
  const MES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN",
               "JUL", "AGO", "SET", "OCT", "NOV", "DIC"]
  const corte = d?.corte?.mes ?? null

  return (
    <WorkPanel
      title={t("Pago de impuestos")}
      meta={
        <span className="text-2xs text-muted-foreground">
          {t("Salida de caja por impuestos · importes en USD")}
        </span>
      }
      footnote={d?.nota}
    >
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!d || (Object.keys(d.meses.real).length === 0
                      && Object.keys(d.meses.proyectado).length === 0)}
        emptyMessage={t("No hay pagos de impuestos en el modelo de este año.")}
        emptyHint={t("El pago de impuestos sale del flujo de caja de tu modelo de gestión. Si la hoja no trae la línea de impuestos, aquí no hay nada que sumar.")}
        onRetry={() => void q.refetch()}
        rows={4}
      >
        {d ? (
          <div className="space-y-3 p-2.5">
            <MetricRow>
              <MetricCard label={t("Pagado en el año")} value={d.anual.real} />
              <MetricCard label={t("Queda por pagar")} value={d.anual.proyectado} />
              <MetricCard label={t("Total del año")} value={d.anual.total}
                          tono="destacado" />
            </MetricRow>

            {/* ------------------------------------------------- mes a mes */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[10rem]">{t("Mes a mes")}</TableHead>
                    {MES.map((m, i) => (
                      <TableHead
                        key={m}
                        /*
                          LA LÍNEA ROJA, igual que en el P&L y por la misma razón:
                          sin ella las dos filas se leen como una sola serie y
                          nada dice dónde dejó de ser un hecho.
                        */
                        className={cn("w-20 text-right",
                                      corte === i + 1 && "corte-real")}
                      >
                        {m}
                      </TableHead>
                    ))}
                    <TableHead className="w-24 text-right">{t("Total")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-xs font-medium">{t("Pagado")}</TableCell>
                    {meses.map((m) => (
                      <TableCell key={m} className="num text-right">
                        {d.meses.real[m] ? formatAmount(d.meses.real[m]) : ""}
                      </TableCell>
                    ))}
                    <TableCell className="num-strong text-right">
                      {formatAmount(d.anual.real)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs font-medium text-muted-foreground">
                      {t("Proyectado")}
                    </TableCell>
                    {meses.map((m) => (
                      <TableCell key={m} className="num text-right text-muted-foreground">
                        {d.meses.proyectado[m] ? formatAmount(d.meses.proyectado[m]) : ""}
                      </TableCell>
                    ))}
                    <TableCell className="num text-right text-muted-foreground">
                      {formatAmount(d.anual.proyectado)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* ------------------------------------------- por trimestre */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[10rem]">{t("Por trimestre")}</TableHead>
                    {d.trimestres.map((q2) => (
                      <TableHead key={q2.trimestre} className="w-28 text-right">
                        {q2.trimestre}
                      </TableHead>
                    ))}
                    <TableHead className="w-28 text-right">{t("Año")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-xs font-medium">{t("Pagado")}</TableCell>
                    {d.trimestres.map((q2) => (
                      <TableCell key={q2.trimestre} className="num text-right">
                        {q2.real ? formatAmount(q2.real) : ""}
                      </TableCell>
                    ))}
                    <TableCell className="num-strong text-right">
                      {formatAmount(d.anual.real)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-xs font-medium text-muted-foreground">
                      {t("Proyectado")}
                    </TableCell>
                    {d.trimestres.map((q2) => (
                      <TableCell key={q2.trimestre} className="num text-right text-muted-foreground">
                        {q2.proyectado ? formatAmount(q2.proyectado) : ""}
                      </TableCell>
                    ))}
                    <TableCell className="num text-right text-muted-foreground">
                      {formatAmount(d.anual.proyectado)}
                    </TableCell>
                  </TableRow>
                  {/*
                    EL TRIMESTRE PARTIDO LLEVA SU AVISO. Un Q con un mes pagado y
                    dos estimados no es ninguna de las dos cosas, y su total
                    sumado se lee como un hecho.
                  */}
                  <TableRow>
                    <TableCell />
                    {d.trimestres.map((q2) => (
                      <TableCell key={q2.trimestre} className="text-right">
                        {q2.mixto ? (
                          <Badge
                            variant="warning-soft"
                            estado
                            title={t("Este trimestre tiene meses pagados y meses estimados. Su total no es ni lo uno ni lo otro.")}
                          >
                            {t("mitad estimado")}
                          </Badge>
                        ) : null}
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* ---------------------------------------------- por sociedad */}
            {d.por_pais.length > 1 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[10rem]">{t("Por país")}</TableHead>
                      <TableHead className="w-32 text-right">{t("Pagado")}</TableHead>
                      <TableHead className="w-32 text-right">{t("Proyectado")}</TableHead>
                      <TableHead className="w-32 text-right">{t("Total")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.por_pais.map((p) => (
                      <TableRow key={p.pais}>
                        <TableCell className="text-xs font-medium">{p.pais}</TableCell>
                        <TableCell className="num text-right">
                          {formatAmount(p.total_real)}
                        </TableCell>
                        <TableCell className="num text-right text-muted-foreground">
                          {formatAmount(p.total_proyectado)}
                        </TableCell>
                        <TableCell className="num-strong text-right">
                          {formatAmount(p.total_real + p.total_proyectado)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {/*
                  UN PAÍS EN CERO NO ES UN PAÍS SIN IMPUESTOS. Es que el modelo no
                  trae su línea de pagos: tres de las cuatro sociedades de Siete
                  están así. Decirlo evita la lectura más cara de esta tabla.
                */}
                {d.por_pais.some((p) => p.total_real + p.total_proyectado === 0) ? (
                  <p className="flex items-start gap-1.5 px-2.5 pt-2 text-2xs leading-5 text-muted-foreground">
                    <Info className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
                    {t("Un país en cero no es un país que no pague impuestos: es que tu modelo de gestión no trae su línea de pagos. Hasta que la traiga, el total del grupo va corto.")}
                  </p>
                ) : null}
              </div>
            ) : null}

            {d.corte?.regla ? (
              <p className="text-2xs leading-5 text-muted-foreground">{d.corte.regla}</p>
            ) : null}
          </div>
        ) : null}
      </DataState>
    </WorkPanel>
  )
}
